// DTOs de C5 — análisis de cumplimiento (coordinador).
//
// Modelo base: vw_cumplimiento_dia_sku_linea (MV mig 00015).
//
// Endpoints:
//   GET /api/coordinador/cumplimiento         — tabla agregada con filtros + granularidad
//   GET /api/coordinador/cumplimiento/linea/:lineaId — drill-down de una línea
//   GET /api/coordinador/cumplimiento/export  — CSV del set filtrado
import { z } from 'zod';
import { produccionPorHoraSchema, produccionPorTurnoSchema } from './coordinador_analisis_paros.js';
// =============================================================================
// Filtros
// =============================================================================
export const granularidadSchema = z.enum(['hora', 'dia', 'semana', 'mes']);
/**
 * Modo de agrupación de la tabla detalle. Default 'linea'.
 * - 'area': cumplimiento vs PROGRAMA agregado por (área, sku) sobre el rango.
 *   Es el nivel correcto para medir el programa de Alpina (la producción se mueve
 *   entre líneas de una misma área). Lee de tablas fuente (no la MV) para no
 *   perder programa de líneas con producción cero.
 * - 'linea': la línea se mide SOLO vs CAPACIDAD (cumplimiento_meta_pct). El
 *   cumplimiento vs programa NO se atribuye a una línea (cantidad_programa = 0,
 *   cumplimiento_pct = null) — vive a nivel área para arriba.
 */
export const agrupacionSchema = z.enum(['linea', 'sku', 'dia', 'area']);
export const cumplimientoQuerySchema = z.object({
    desde: z.string().date().optional(),
    hasta: z.string().date().optional(),
    granularidad: granularidadSchema.optional().default('dia'),
    agrupacion: agrupacionSchema.optional().default('linea'),
    cliente_id: z.string().uuid().optional(),
    planta_id: z.string().uuid().optional(),
    area_id: z.string().uuid().optional(),
    linea_id: z.string().uuid().optional(),
    sku_id: z.string().uuid().optional(),
    /**
     * Filtro por turno (número operativo 1..9, mismo eje que S9). Con turno el
     * bloque agrega desde tablas base (la MV no tiene dimensión turno) y el
     * cumplimiento vs PROGRAMA no aplica (el programa es diario) → queda null.
     * No combinable con agrupacion='area' (el cruce vs programa es diario).
     */
    turno_numero: z.coerce.number().int().min(1).max(9).optional(),
    /**
     * Base de validación del lado REAL: 'capturado' (default — estado='valido',
     * el comportamiento histórico) o 'validada' (además exige validado_at, es
     * decir validado por supervisor — V1, la usa el informe gerencial en TODAS
     * sus consultas de producción). Soportada en todas las agrupaciones: con
     * 'validada' el cálculo fuerza el path de tablas base (las MVs no distinguen
     * validación) y las distribuciones por turno/hora usan la misma base. Las
     * corridas de tiendas entran con su producción validada, pero NO reciben el
     * programa derivado (programado := producido) del carril de tiendas.
     */
    base: z.enum(['capturado', 'validada']).optional(),
    /** Si true, calcula período anterior simétrico y devuelve `comparativo`. */
    comparar: z
        .union([z.boolean(), z.literal('true'), z.literal('false')])
        .optional()
        .transform((v) => v === true || v === 'true')
        .pipe(z.boolean().optional()),
});
// =============================================================================
// Recursos para los selects (filtros)
// =============================================================================
const clienteResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
});
const plantaResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    codigo: z.string(),
});
const areaResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    planta_id: z.string().uuid(),
});
const lineaResumenSchema = z.object({
    id: z.string().uuid(),
    codigo: z.string(),
    nombre: z.string(),
    planta_id: z.string().uuid(),
    area_id: z.string().uuid(),
});
const skuResumenSchema = z.object({
    id: z.string().uuid(),
    cliente_id: z.string().uuid(),
    codigo_cliente: z.string(),
    descripcion: z.string(),
    /** Líneas donde corre este SKU (de skus_lineas, dentro del scope del coordinador). */
    linea_ids: z.array(z.string().uuid()),
});
// =============================================================================
// Fila agregada (por granularidad × línea)
// =============================================================================
export const cumplimientoFilaSchema = z.object({
    /** Etiqueta del período según granularidad: '2026-05-25 14' (hora), '2026-05-25' (día), '2026-W22' (semana), '2026-05' (mes). */
    periodo: z.string(),
    linea_id: z.string().uuid(),
    linea_codigo: z.string(),
    linea_nombre: z.string(),
    planta_nombre: z.string(),
    /** Solo cuando agrupacion='area': identifica el área de la fila. null en el resto. */
    area_id: z.string().uuid().nullable(),
    area_nombre: z.string().nullable(),
    /** Solo cuando agrupacion='sku': SKU desagregado de la fila. */
    sku_id: z.string().uuid().nullable(),
    sku_codigo: z.string().nullable(),
    sku_descripcion: z.string().nullable(),
    cantidad_real: z.number().min(0),
    /**
     * null cuando el filtro es por LÍNEA: el programa se carga por (área, sku) y
     * no es atribuible a una línea — se declara "no disponible", nunca 0 (V2).
     */
    cantidad_programa: z.number().min(0).nullable(),
    /**
     * Producción real cuyo (área, sku) NO estaba en el programa vigente del rango.
     * Solo se computa en agrupacion='area' (0 en el resto). Se muestra aparte,
     * NUNCA dentro del % de cumplimiento — es "producido fuera de plan".
     */
    producido_fuera_plan: z.number().min(0),
    /** Meta = capacidad_estandar_hora × bloques_capturados (capacidad instalada). */
    meta_acumulada: z.number().min(0),
    cumplimiento_pct: z.number().min(0).nullable(),
    /** Cumplimiento vs meta (capacidad), independiente del programa. */
    cumplimiento_meta_pct: z.number().min(0).nullable(),
    /** meta_acumulada - cantidad_real cuando positivo, 0 si superó la meta. */
    producidos_perdidos: z.number().min(0),
    minutos_paro: z.number().min(0),
    bloques_capturados: z.number().int().min(0),
    /** Horas trabajadas = bloques_capturados (cada bloque = 1 hora). */
    horas_trabajadas: z.number().min(0),
    /** Horas de paro = minutos_paro / 60 (2 decimales). */
    horas_paro: z.number().min(0),
    /** Causal MADRE dominante del grupo. Solo para pivots 'linea' y 'dia' (null en 'sku'). */
    top_causal_nombre: z.string().nullable(),
    /** COUNT(DISTINCT línea) del grupo. Solo para pivots 'dia' y 'sku' (null en 'linea'). */
    lineas_count: z.number().int().nullable(),
    /**
     * Solo en la fila sintética "Tiendas": el programado se DERIVA en lectura
     * (programado := producido ⇒ cumplimiento 100 %) — decisión interina hasta
     * que exista programación real de tiendas. Ausente en filas de líneas.
     */
    origen_programa: z.literal('autogenerado').optional(),
    // --- Rentabilidad (WS-5 B2). null/ausente si el usuario no puede ver costos. ---
    /** Valor producido = Σ(cantidad_real × precio_vigente de la planta). */
    valor_producido: z.number().nullable().optional(),
    /** Costo de mano de obra = Σ(operarios × hora_hombre_vigente de la planta). */
    costo_mano_obra: z.number().nullable().optional(),
    /** Utilidad = valor_producido − costo_mano_obra. */
    utilidad: z.number().nullable().optional(),
    /** % utilidad = utilidad / valor_producido × 100. null si valor_producido = 0. */
    utilidad_pct: z.number().nullable().optional(),
});
// =============================================================================
// Totales agregados (del set filtrado)
// =============================================================================
// =============================================================================
// Sin tarifa (DN9, 2026-09-08): producción cuyo SKU no tiene precio vigente.
// NO suma ni resta a la utilidad; se declara con su tamaño para que el hueco de
// tarifas sea visible y no se disfrace de pérdida.
// =============================================================================
export const sinTarifaSchema = z.object({
    bloques: z.number().int().min(0),
    paquetes: z.number().min(0),
    skus: z.number().int().min(0),
    /** Mano de obra gastada en ese producto, declarada (no restada). */
    costo_mano_obra: z.number().min(0),
});
export const sinTarifaTiendaSchema = z.object({
    corridas: z.number().int().min(0),
    paquetes: z.number().min(0),
    skus: z.number().int().min(0),
    costo_mano_obra: z.number().min(0),
    /** Insumos cobrados en esas corridas — también fuera del cálculo, declarados. */
    ingreso_insumos: z.number().min(0),
});
/** Bloques/corridas con precio pero SIN costo hora-persona vigente: fuera del cálculo, declarados. */
export const sinCostoMoSchema = z.object({
    bloques: z.number().int().min(0),
    paquetes: z.number().min(0),
    valor_producido: z.number().min(0),
});
export const sinCostoMoTiendaSchema = z.object({
    corridas: z.number().int().min(0),
    paquetes: z.number().min(0),
    valor_producido: z.number().min(0),
});
/**
 * Conciliación validado ↔ capturado (solo cuando base=validada): lo que falta
 * por validar, con la misma fórmula. validado + pendiente = capturado (lo que
 * ve el directivo).
 */
export const pendienteValidacionUtilidadSchema = z.object({
    /**
     * Bloques (corridas en tiendas) sin validar que entraron al cálculo. Es la
     * UNIDAD de validación: un bloque de 0 paquetes puede costar mano de obra, así
     * que "¿queda algo pendiente?" se decide por acá, NUNCA por `paquetes`.
     */
    bloques: z.number().int().min(0),
    paquetes: z.number().min(0),
    valor_producido: z.number().min(0),
    costo_mano_obra: z.number().min(0),
    utilidad: z.number(),
});
export const cumplimientoTotalesSchema = z.object({
    cantidad_real_total: z.number().min(0),
    /** null con filtro de línea: programa no atribuible por línea (V2). */
    cantidad_programa_total: z.number().min(0).nullable(),
    /**
     * true cuando el response se filtró por línea y por eso el programa y el
     * cumplimiento vs programa vienen en null. El FE lo declara en pantalla.
     */
    programa_por_linea_no_disponible: z.boolean().optional(),
    /** SUM(capacidad × bloques) — capacidad instalada total del rango. */
    meta_acumulada_total: z.number().min(0),
    /** Suma de pérdidas vs meta (meta - real cuando positivo). */
    producidos_perdidos_total: z.number().min(0),
    /** Ponderado por programa: SUM(real)/SUM(programa)*100. null si no hay programa. */
    cumplimiento_promedio_pct: z.number().min(0).nullable(),
    /** Ponderado por meta: SUM(real)/SUM(meta)*100. null si no hay meta. */
    cumplimiento_meta_promedio_pct: z.number().min(0).nullable(),
    /**
     * Total de producción real fuera del programa vigente del rango (agrupacion='area').
     * 0 en el resto de agrupaciones. NO entra en cumplimiento_promedio_pct.
     */
    producido_fuera_plan_total: z.number().min(0),
    minutos_paro_total: z.number().min(0),
    lineas_distintas: z.number().int().min(0),
    dias_capturados: z.number().int().min(0),
    /** # de combinaciones (línea × día) con cumpl_meta < 60% (threshold rojo canónico). */
    bajo_bajo_meta_count: z.number().int().min(0),
    /** Total de combinaciones (línea × día) capturadas en el rango — denominador de bajo_bajo_meta. */
    bajo_bajo_meta_universo: z.number().int().min(0),
    // --- Rentabilidad (WS-5 B2). null/ausente si el usuario no puede ver costos. ---
    // DN9 (2026-09-08): MISMA fórmula que el directivo (lib/utilidad-base): MO por
    // horas reales, solo bloques con tarifa vigente; lo sin tarifa va aparte.
    valor_producido_total: z.number().nullable().optional(),
    costo_mano_obra_total: z.number().nullable().optional(),
    utilidad_total: z.number().nullable().optional(),
    utilidad_pct_total: z.number().nullable().optional(),
    /** Producción sin precio vigente: fuera del cálculo de utilidad, declarada. */
    sin_tarifa_total: sinTarifaSchema.nullable().optional(),
    /** Bloques sin costo hora-persona vigente: fuera del cálculo, declarados. */
    sin_costo_mo_total: sinCostoMoSchema.nullable().optional(),
    /** Paquetes que SÍ entraron al cálculo de utilidad (con precio y costo MO). */
    paquetes_utilidad_total: z.number().min(0).nullable().optional(),
    /** Base efectiva de la utilidad ('validada' solo si el query lo pidió). */
    base_utilidad: z.enum(['capturado', 'validada']).optional(),
    /** Solo con base=validada: lo pendiente de validar (validado + esto = capturado). */
    pendiente_validacion_utilidad: pendienteValidacionUtilidadSchema.nullable().optional(),
});
// =============================================================================
// Punto de la serie temporal (para el gráfico Composed del FE)
// =============================================================================
export const cumplimientoSeriePuntoSchema = z.object({
    periodo: z.string(),
    cantidad_real: z.number().min(0),
    /** null con filtro de línea (programa no atribuible por línea, V2). */
    cantidad_programa: z.number().min(0).nullable(),
    meta_acumulada: z.number().min(0),
    cumplimiento_pct: z.number().min(0).nullable(),
    cumplimiento_meta_pct: z.number().min(0).nullable(),
    // --- Rentabilidad (WS-5 B2/B3). null/ausente si no puede ver costos. ---
    /** Utilidad del período. */
    utilidad: z.number().nullable().optional(),
    /** Valor de venta del período (Σ cantidad × precio). */
    valor_producido: z.number().nullable().optional(),
    /** Costo de mano de obra del período. */
    costo_mano_obra: z.number().nullable().optional(),
    /** % utilidad del período (utilidad ÷ valor de venta). */
    utilidad_pct: z.number().nullable().optional(),
});
// =============================================================================
// Utilidad por área (WS-5 B3) — para el gráfico de barras, independiente de la
// agrupación de la tabla. Solo presente si el usuario puede ver costos.
// =============================================================================
export const utilidadAreaSchema = z.object({
    area_id: z.string().uuid(),
    area_nombre: z.string(),
    valor_producido: z.number(),
    costo_mano_obra: z.number(),
    utilidad: z.number(),
    utilidad_pct: z.number().nullable(),
    sin_tarifa: sinTarifaSchema.optional(),
    sin_costo_mo: sinCostoMoSchema.optional(),
});
// =============================================================================
// Rentabilidad de Tiendas (WS-5) — bloque consolidado dentro de la vista
// Rentabilidad de C5, para que la plata de líneas + tiendas viva en una sola
// pantalla. Cálculo por corrida de reempaque (registros_tienda):
//   valor_unidades  = unidades × precio_sku (mismo precio que producción)
//   ingreso_insumos = stickers × tarifa_sticker + termos × tarifa_termo
//   valor_producido = valor_unidades + ingreso_insumos   (total cobrado)
//   costo           = operarios × (duración_min / 60) × hora-hombre (solo MO)
//   utilidad        = valor_producido − costo_mano_obra
// Regla de negocio (UPG 2026-07-02): los insumos de tienda se COBRAN ADICIONAL
// al cliente — son ingreso, no un costo que resta utilidad.
// Presente solo si el usuario puede ver costos. NO aplica a filtros de
// área/línea/cliente (las tiendas no pertenecen a una línea) → null en ese caso.
// =============================================================================
const rentabilidadTiendaAggSchema = z.object({
    /** Unidades reempacadas × precio vigente del SKU. */
    valor_unidades: z.number(),
    /** Stickers y termos cobrados adicional al cliente (cantidad × tarifa). */
    ingreso_insumos: z.number(),
    /** valor_unidades + ingreso_insumos = total cobrado. */
    valor_producido: z.number(),
    costo_mano_obra: z.number(),
    utilidad: z.number(),
    utilidad_pct: z.number().nullable(),
    /** Paquetes que entraron al cálculo (con precio y costo MO). */
    paquetes: z.number().min(0).optional(),
    /** DN9: corridas con SKU sin precio — fuera del cálculo, declaradas. */
    sin_tarifa: sinTarifaTiendaSchema.optional(),
    sin_costo_mo: sinCostoMoTiendaSchema.optional(),
});
export const rentabilidadTiendaDestinoSchema = rentabilidadTiendaAggSchema.extend({
    tienda_id: z.string().uuid(),
    tienda_codigo: z.string(),
    tienda_nombre: z.string(),
});
export const rentabilidadTiendaSeriePuntoSchema = rentabilidadTiendaAggSchema.extend({
    periodo: z.string(),
});
export const rentabilidadTiendasSchema = z.object({
    total: rentabilidadTiendaAggSchema,
    por_destino: z.array(rentabilidadTiendaDestinoSchema),
    serie_temporal: z.array(rentabilidadTiendaSeriePuntoSchema),
    /** Base efectiva ('validada' solo si el query lo pidió) — misma que las líneas. */
    base: z.enum(['capturado', 'validada']).optional(),
    /** Solo con base=validada: corridas pendientes de validar (validado + esto = capturado). */
    pendiente_validacion: pendienteValidacionUtilidadSchema.nullable().optional(),
});
// =============================================================================
// Comparativo período anterior simétrico
// =============================================================================
export const cumplimientoComparativoSchema = z.object({
    desde: z.string().date(),
    hasta: z.string().date(),
    totales: cumplimientoTotalesSchema,
    /** Delta porcentual puntual: actual - anterior (en puntos porcentuales). */
    delta_cumplimiento_pct: z.number().nullable(),
    delta_cumplimiento_meta_pct: z.number().nullable(),
    /** Delta absoluto en producidos perdidos. */
    delta_producidos_perdidos: z.number(),
    delta_bajo_bajo_meta_count: z.number().int(),
});
// =============================================================================
// Response GET /api/coordinador/cumplimiento
// =============================================================================
export const coordinadorCumplimientoSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    scope: z.object({
        plantas_asignadas: z.array(z.object({ id: z.string().uuid(), nombre: z.string(), codigo: z.string() })),
    }),
    filtros: z.object({
        desde: z.string().date(),
        hasta: z.string().date(),
        granularidad: granularidadSchema,
        agrupacion: agrupacionSchema,
        cliente_id: z.string().uuid().nullable(),
        planta_id: z.string().uuid().nullable(),
        area_id: z.string().uuid().nullable(),
        linea_id: z.string().uuid().nullable(),
        sku_id: z.string().uuid().nullable(),
        /** Turno operativo filtrado (1..9). null = todos los turnos (comportamiento clásico). */
        turno_numero: z.number().int().nullable(),
    }),
    /** Última actualización de la MV (max created_at de registros válidos). */
    ultima_actualizacion: z.string().nullable(),
    recursos: z.object({
        clientes: z.array(clienteResumenSchema),
        plantas: z.array(plantaResumenSchema),
        areas: z.array(z.object({
            id: z.string().uuid(),
            nombre: z.string(),
            planta_id: z.string().uuid(),
            /** # de líneas activas del área (para mostrar en el dropdown de filtro). */
            lineas_count: z.number().int().min(0),
        })),
        lineas: z.array(lineaResumenSchema),
        skus: z.array(skuResumenSchema),
        /** Turnos del scope deduplicados por número (mismo eje que S9). */
        turnos: z.array(z.object({ numero: z.number().int(), nombre: z.string() })),
    }),
    totales: cumplimientoTotalesSchema,
    filas: z.array(cumplimientoFilaSchema),
    serie_temporal: z.array(cumplimientoSeriePuntoSchema),
    /**
     * Utilidad por área del rango (WS-5 B3), para el gráfico de barras. Siempre
     * por área, independiente de la agrupación de la tabla. null/ausente si el
     * usuario no puede ver costos.
     */
    utilidad_por_area: z.array(utilidadAreaSchema).nullable().optional(),
    /**
     * Rentabilidad de tiendas del rango (WS-5), para consolidar la plata de la
     * planta (líneas + tiendas) en la vista Rentabilidad. null/ausente si el
     * usuario no puede ver costos o si hay filtro de área/línea/cliente (no aplica
     * a tiendas).
     */
    rentabilidad_tiendas: rentabilidadTiendasSchema.nullable().optional(),
    /**
     * Intensidad de la operación — producción/cumplimiento agregado por turno y
     * por hora del rango, SIEMPRE con los mismos filtros del bloque principal
     * (cliente/planta/área/línea/sku/turno) pero independiente de la
     * `granularidad`/`agrupacion` elegida para la tabla. Mismo criterio que
     * `/analisis-paros`: sin cumplimiento vs. programa (el programa es diario).
     */
    produccion_por_turno: z.array(produccionPorTurnoSchema),
    produccion_por_hora: z.array(produccionPorHoraSchema),
    /** Presente solo si query.comparar === true. */
    comparativo: cumplimientoComparativoSchema.nullable(),
});
// =============================================================================
// Drill-down de una línea: detalle por (fecha, sku)
// =============================================================================
export const cumplimientoLineaFilaSchema = z.object({
    fecha: z.string().date(),
    sku_id: z.string().uuid(),
    sku_codigo_cliente: z.string(),
    sku_descripcion: z.string(),
    cliente_nombre: z.string(),
    cantidad_real: z.number().min(0),
    cantidad_programa: z.number().min(0),
    cumplimiento_pct: z.number().min(0).nullable(),
    bloques_capturados: z.number().int().min(0),
    minutos_paro: z.number().min(0),
});
export const coordinadorCumplimientoLineaSchema = z.object({
    linea: z.object({
        id: z.string().uuid(),
        codigo: z.string(),
        nombre: z.string(),
        planta_nombre: z.string(),
    }),
    filtros: z.object({
        desde: z.string().date(),
        hasta: z.string().date(),
    }),
    totales: cumplimientoTotalesSchema,
    filas: z.array(cumplimientoLineaFilaSchema),
});
//# sourceMappingURL=coordinador_cumplimiento.js.map